import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  Cast,
  CastCreateRequest,
  CastUpdateRequest,
  CastsPage,
  CastsQueryParams,
  ContentCastCreateRequest,
} from '../../types/api/cast';
import type { ContentCastResponse, DramaCast } from '../../types/api/drama';

/**
 * Cast Service
 *
 * Handles:
 * - Cast CRUD operations (admin)
 * - Public cast listing & search
 * - Assigning casts to movies
 *
 * NOTE:
 * - Drama/Series cast assignment is handled in `DramaService`.
 * - This service focuses on cast-centric operations and movie-level assignment.
 */
export class CastService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = HttpClient.getInstance();
  }

  // ===========================================================================
  // Cast CRUD (Admin)
  // ===========================================================================

  /**
   * Create a new cast with optional profile image (multipart/form-data).
   *
   * Parts:
   * - data: JSON string (CastCreateRequest)
   * - profileImage: optional image file
   */
  async createCastWithImage(
    payload: CastCreateRequest,
    profileImageFile?: File
  ): Promise<Cast> {
    const formData = new FormData();

    const jsonBlob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    formData.append('data', jsonBlob, 'data.json');

    if (profileImageFile) {
      formData.append('profileImage', profileImageFile, profileImageFile.name);
    }

    const response = await this.httpClient.request<Cast>({
      method: 'POST',
      url: API_ENDPOINTS.CASTS.ADMIN_CREATE_MULTIPART,
      data: formData,
      headers: {
        // HttpClient + interceptors will handle multipart boundary correctly.
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create cast');
    }

    return response.data;
  }

  /**
   * Create a new cast using JSON-only endpoint.
   */
  async createCastWithJson(payload: CastCreateRequest): Promise<Cast> {
    const response = await this.httpClient.post<Cast>(
      API_ENDPOINTS.CASTS.ADMIN_CREATE_JSON,
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to create cast');
    }

    return response.data;
  }

  /**
   * Update existing cast with optional new profile image (multipart/form-data).
   */
  async updateCastWithImage(
    castId: string,
    payload: CastUpdateRequest,
    profileImageFile?: File
  ): Promise<Cast> {
    const formData = new FormData();

    const jsonBlob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    formData.append('data', jsonBlob, 'data.json');

    if (profileImageFile) {
      formData.append('profileImage', profileImageFile, profileImageFile.name);
    }

    const response = await this.httpClient.request<Cast>({
      method: 'PUT',
      url: API_ENDPOINTS.CASTS.ADMIN_UPDATE_MULTIPART(castId),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update cast');
    }

    return response.data;
  }

  /**
   * Update existing cast using JSON-only endpoint.
   */
  async updateCastWithJson(castId: string, payload: CastUpdateRequest): Promise<Cast> {
    const response = await this.httpClient.put<Cast>(
      API_ENDPOINTS.CASTS.ADMIN_UPDATE_JSON(castId),
      payload
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to update cast');
    }

    return response.data;
  }

  /**
   * Delete cast (hard delete).
   */
  async deleteCast(castId: string): Promise<void> {
    const response = await this.httpClient.delete<{ message: string }>(
      API_ENDPOINTS.CASTS.ADMIN_DELETE(castId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete cast');
    }
  }

  // ===========================================================================
  // Public Cast Read APIs
  // ===========================================================================

  /**
   * Get cast by ID.
   */
  async getCast(castId: string): Promise<Cast> {
    const response = await this.httpClient.get<Cast>(
      API_ENDPOINTS.CASTS.PUBLIC_GET(castId)
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch cast');
    }

    return response.data;
  }

  /**
   * Get paginated list of casts.
   *
   * Backend expects 1-based page index (page >= 1).
   * Converts 0-based or undefined page to 1-based.
   */
  async getCasts(params: CastsQueryParams = {}): Promise<CastsPage> {
    const queryParams = new URLSearchParams();

    // Backend expects 1-based pagination (page >= 1)
    // Convert 0-based or undefined to 1-based
    if (params.page !== undefined) {
      const page = params.page > 0 ? params.page : 1;
      queryParams.append('page', page.toString());
    } else {
      queryParams.append('page', '1');
    }
    
    if (params.size !== undefined) {
      queryParams.append('size', params.size.toString());
    }
    if (params.keyword) {
      queryParams.append('keyword', params.keyword);
    }
    if (params.nationality) {
      queryParams.append('nationality', params.nationality);
    }

    const baseUrl = API_ENDPOINTS.CASTS.ADMIN_LIST;
    const url =
      queryParams.toString().length > 0 ? `${baseUrl}?${queryParams.toString()}` : baseUrl;

    const response = await this.httpClient.get<CastsPage>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch casts');
    }

    return response.data;
  }

  /**
   * Search casts by keyword.
   *
   * Backend expects 1-based page index (page >= 1).
   * Converts 0-based or undefined page to 1-based.
   */
  async searchCasts(
    keyword: string,
    params: { page?: number; size?: number } = {}
  ): Promise<CastsPage> {
    const queryParams = new URLSearchParams();

    queryParams.append('keyword', keyword);
    
    // Backend expects 1-based pagination (page >= 1)
    // Convert 0-based or undefined to 1-based
    const page = params.page !== undefined && params.page > 0 ? params.page : 1;
    queryParams.append('page', page.toString());
    
    if (params.size !== undefined) {
      queryParams.append('size', params.size.toString());
    }

    const baseUrl = API_ENDPOINTS.CASTS.PUBLIC_SEARCH;
    const url = `${baseUrl}?${queryParams.toString()}`;

    const response = await this.httpClient.get<CastsPage>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to search casts');
    }

    return response.data;
  }

  /**
   * Get casts by nationality (non-paginated).
   */
  async getCastsByNationality(nationality: string): Promise<Cast[]> {
    const url = API_ENDPOINTS.CASTS.PUBLIC_BY_NATIONALITY(nationality);
    const response = await this.httpClient.get<Cast[]>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch casts by nationality');
    }

    return response.data;
  }

  /**
   * Get popular casts ordered by number of associated contents.
   */
  async getPopularCasts(minContentCount?: number): Promise<Cast[]> {
    const baseUrl = API_ENDPOINTS.CASTS.PUBLIC_POPULAR;

    const url =
      typeof minContentCount === 'number'
        ? `${baseUrl}?minContentCount=${encodeURIComponent(minContentCount)}`
        : baseUrl;

    const response = await this.httpClient.get<Cast[]>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch popular casts');
    }

    return response.data;
  }

  // ===========================================================================
  // Movie Cast Assignment
  // ===========================================================================

  /**
   * Assign a cast to a movie.
   */
  async assignCastToMovie(
    movieId: string,
    payload: ContentCastCreateRequest
  ): Promise<ContentCastResponse> {
    const url = API_ENDPOINTS.MOVIES.ADMIN_ASSIGN_CAST(movieId);
    const response = await this.httpClient.post<ContentCastResponse>(url, payload);

    if (!response.success) {
      throw new Error(response.message || 'Failed to assign cast to movie');
    }

    return response.data;
  }

  /**
   * Remove a cast from a movie.
   */
  async removeCastFromMovie(movieId: string, castId: string): Promise<void> {
    const url = API_ENDPOINTS.MOVIES.ADMIN_REMOVE_CAST(movieId, castId);
    const response = await this.httpClient.delete<{ message: string }>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to remove cast from movie');
    }
  }

  /**
   * Update role of a cast member in a movie.
   */
  async updateMovieCastRole(
    movieId: string,
    castId: string,
    role: string
  ): Promise<ContentCastResponse> {
    const baseUrl = API_ENDPOINTS.MOVIES.ADMIN_UPDATE_CAST_ROLE(movieId, castId);
    const url = `${baseUrl}?role=${encodeURIComponent(role)}`;

    const response = await this.httpClient.put<ContentCastResponse>(url, undefined);

    if (!response.success) {
      throw new Error(response.message || 'Failed to update movie cast role');
    }

    return response.data;
  }

  /**
   * List all casts for a given movie.
   *
   * Uses the public movie casts endpoint which returns `CastDetailResponse`
   * matching the `DramaCast` shape used for drama/series casts.
   */
  async getMovieCasts(movieId: string): Promise<DramaCast[]> {
    const url = API_ENDPOINTS.MOVIES.PUBLIC_LIST_CASTS(movieId);
    const response = await this.httpClient.get<DramaCast[]>(url);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch movie casts');
    }

    return response.data;
  }
}


